import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Award, HeartHandshake, Search, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import Reveal from "../components/Reveal";
import Counter from "../components/Counter";
import { ABOUT } from "../mock";

const iconMap = { Award, HeartHandshake, Search };

const About = () => {
  return (
    <div>
      {/* INTRO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-sky-50 to-white py-16 lg:py-24">
        <div className="pointer-events-none absolute -left-20 top-10 h-72 w-72 rounded-full bg-sky-200/40 blur-3xl animate-blob" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2 lg:px-8">
          <Reveal variant="left">
            <span className="text-sm font-semibold uppercase tracking-wider text-sky-600">À propos</span>
            <h1 className="mt-3 text-3xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-4xl">
              {ABOUT.heading}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">{ABOUT.intro}</p>
            <div className="mt-6 flex flex-wrap items-center gap-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm">
                <MapPin size={18} className="text-sky-500" /> 22 rue Cafarelli, 31000 Toulouse
              </div>
              <div className="flex items-baseline gap-1">
                <Counter value="30" className="text-3xl font-extrabold text-sky-500" />
                <span className="text-sm font-semibold text-slate-600">ans d'expérience</span>
              </div>
            </div>
          </Reveal>
          <Reveal variant="right" className="grid grid-cols-2 gap-4">
            <img src={ABOUT.images[0]} alt="Nettoyage professionnel" className="h-64 w-full rounded-2xl object-cover shadow-lg transition-transform duration-500 hover:scale-[1.03] lg:h-80" />
            <img src={ABOUT.images[1]} alt="Nettoyage professionnel" className="mt-8 h-64 w-full rounded-2xl object-cover shadow-lg transition-transform duration-500 hover:scale-[1.03] lg:h-80" />
          </Reveal>
        </div>
      </section>

      {/* STORY */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-3xl space-y-6 px-5 lg:px-8">
          {ABOUT.paragraphs.map((p, i) => (
            <Reveal as="p" key={i} delay={i * 120} className="text-lg leading-relaxed text-slate-600">{p}</Reveal>
          ))}
        </div>
      </section>

      {/* APPROCHE */}
      <section className="bg-sky-50 py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="mx-auto mb-14 max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-sky-600">Notre approche</span>
            <h2 className="mt-3 text-3xl font-bold text-slate-900 sm:text-4xl">
              Trois piliers qui guident notre travail.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {ABOUT.pillars.map((p, i) => {
              const Icon = iconMap[p.icon];
              return (
                <Reveal key={p.title} variant="up" delay={i * 130}>
                  <div className="card-lift group h-full rounded-2xl bg-white p-8 shadow-sm hover:shadow-lg">
                    <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-xl bg-sky-500 text-white transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                      <Icon size={26} />
                    </div>
                    <h3 className="mb-3 text-xl font-bold text-slate-900">{p.title}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{p.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <div className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {ABOUT.gallery.map((img, i) => (
              <Reveal key={i} variant="scale" delay={i * 100}>
                <img src={img} alt="Service de nettoyage" className="h-44 w-full rounded-2xl object-cover shadow-sm transition-transform duration-500 hover:scale-[1.05] lg:h-56" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-gradient-to-br from-sky-500 to-sky-600 py-16 lg:py-20">
        <div className="pointer-events-none absolute -right-10 top-0 h-64 w-64 rounded-full bg-white/10 blur-2xl animate-blob" />
        <Reveal className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
          <h2 className="text-3xl font-bold text-white sm:text-4xl">
            Confiez votre nettoyage à une équipe qui en a vu d'autres.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-sky-50">
            30 ans d'expertise au service de votre confort. Demandez votre devis
            dès aujourd'hui.
          </p>
          <div className="mt-8">
            <Link to="/contact">
              <Button className="btn-shine h-12 rounded-full bg-white px-8 text-base font-semibold text-sky-600 shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-sky-50">
                Demander un devis <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default About;
